import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../core/contexts/AuthContext';
import { apiVerifyOtp, setAccessToken } from '../core/services/api.js';
import { getDefaultRedirectPath, hasCompletedSetup } from '../core/utils/authHelpers';
import Spinner from '../shared/components/ui/Spinner';

export default function AuthCallback() {
  const { t } = useTranslation('auth');
  const { user, userRoles, loading, refreshRoles, rehydrate } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);

  useEffect(() => {
    const otp = searchParams.get('otp');

    if (otp) {
      // Magic-link OTP flow
      apiVerifyOtp(otp)
        .then(async (data) => {
          setAccessToken(data.access_token);
          await rehydrate();
        })
        .catch((err) => {
          setError(err.message || t('callback.error.invalidToken'));
        });
    }
  }, []);

  // Redirect once we have a logged-in user
  useEffect(() => {
    if (!loading && user && !error) {
      if (!hasCompletedSetup(userRoles)) {
        navigate('/setup', { replace: true });
      } else {
        navigate(getDefaultRedirectPath(userRoles), { replace: true });
      }
    }
  }, [user, userRoles, loading, error, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md p-6">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {t('callback.heading.signInFailed')}
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="text-indigo-600 hover:underline text-sm"
          >
            {t('callback.link.backToLogin')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <Spinner size="lg" text={false} className="py-0" />
        <h2 className="mt-4 text-xl font-semibold text-gray-900">
          {t('callback.heading.completingSignIn')}
        </h2>
        <p className="mt-2 text-gray-600">{t('callback.text.pleaseWait')}</p>
      </div>
    </div>
  );
}
